import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Elements, ExpressCheckoutElement, PaymentElement, useElements, useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import { ApiRequestError } from '../api/client';
import { api } from '../api/shop';
import { formatMoney } from '../brand/brand';
import { usePageTitle } from '../a11y/usePageTitle';
import type { CounterPayment, CounterPaymentIntent } from '../api/types';

/**
 * The rest of the bill, on the customer's own phone. SOW TC-06 split tender ("the
 * remainder is paid by card, Apple Pay / Google Pay") and SOW 2.6 ("option for the
 * customer to add a tip at payment, passed through to the partner").
 *
 * The cashier's till showed a QR to this page after the voucher paid what it
 * could. Signed in as the voucher's owner - the API answers 404 to anybody else -
 * the customer picks a tip and pays. The till watches for the payment and says
 * "Paid" when Stripe confirms.
 */

const TIP_PERCENTS = [0, 10, 15, 20] as const;

export function PayAtCounter() {
  const { paymentId } = useParams<{ paymentId: string }>();
  const [payment, setPayment] = useState<CounterPayment | null>(null);
  const [failed, setFailed] = useState(false);

  const [tipChoice, setTipChoice] = useState<number | 'custom'>(0);
  const [customTip, setCustomTip] = useState('');
  const [intent, setIntent] = useState<CounterPaymentIntent | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  usePageTitle('Pay the rest of your bill');

  const load = useCallback(() => {
    if (paymentId === undefined) return;
    api.counterPayment(paymentId).then(setPayment).catch(() => setFailed(true));
  }, [paymentId]);

  useEffect(load, [load]);

  // Once they have paid, watch for the webhook so the page can say so for certain.
  useEffect(() => {
    if (payment === null || payment.status !== 'processing') return undefined;
    const timer = window.setInterval(load, 3000);
    return () => window.clearInterval(timer);
  }, [payment, load]);

  const stripePromise = useMemo<Promise<Stripe | null> | null>(
    () => (intent === null ? null : loadStripe(intent.publishableKey)),
    [intent],
  );

  if (failed) {
    return (
      <div className="panel narrow" style={{ marginTop: 40 }}>
        <h2>Payment not found</h2>
        <p className="muted">
          Sign in with the account that holds the voucher you just used. If you did, ask the cashier to check.
        </p>
      </div>
    );
  }

  if (payment === null) {
    return <p className="muted" style={{ padding: '48px 0' }} role="status">Loading…</p>;
  }

  if (payment.status === 'paid') {
    return (
      <div className="panel narrow stack center" style={{ marginTop: 40, gap: 10 }}>
        <h2>Paid - thank you</h2>
        <p className="price">{formatMoney(payment.capturedCents ?? payment.amountCents)}</p>
        <p className="muted">
          {payment.businessName}{payment.tipCents > 0 ? `, including a ${formatMoney(payment.tipCents)} tip` : ''}.
          Show this screen to the cashier if they ask.
        </p>
        <Link to="/vouchers" className="btn secondary">My vouchers</Link>
      </div>
    );
  }

  if (payment.status === 'cancelled' || payment.status === 'expired') {
    return (
      <div className="panel narrow" style={{ marginTop: 40 }}>
        <h2>Nothing to pay here</h2>
        <p className="muted">
          {payment.status === 'expired'
            ? 'This request has expired. Settle the rest with the cashier.'
            : 'The cashier has closed this request - it was settled another way.'}
        </p>
      </div>
    );
  }

  const tipCents = tipChoice === 'custom'
    ? Math.max(0, Math.round((Number(customTip) || 0) * 100))
    // A percentage of the whole bill, the way people think about tipping - not of
    // the part left after the voucher.
    : Math.round((payment.billCents * tipChoice) / 100);

  const total = payment.amountCents + tipCents;

  async function start() {
    if (paymentId === undefined) return;
    setStarting(true);
    setError(null);
    try {
      setIntent(await api.startCounterPayment(paymentId, tipCents));
    } catch (ex) {
      setError(
        ex instanceof ApiRequestError && ex.code === 'PAYMENT_STILL_DECIDING'
          ? // An earlier attempt (theirs, or the shop's Tap to Pay) is still with Stripe.
            'Your payment is still being confirmed. Wait a moment and try again.'
          : ex instanceof ApiRequestError
            ? ex.message
            : 'Could not start the payment.',
      );
    } finally {
      setStarting(false);
    }
  }

  return (
    <div className="stack narrow gap-lg" style={{ marginBlock: 32 }}>
      <div className="panel stack" style={{ gap: 8 }}>
        <p className="faint">{payment.businessName}{payment.branchName !== null ? ` · ${payment.branchName}` : ''}</p>
        <h2>Pay the rest of your bill</h2>
        <dl className="receipt-lines">
          <div><dt>Bill</dt><dd>{formatMoney(payment.billCents)}</dd></div>
          <div><dt>Paid from your voucher</dt><dd>−{formatMoney(payment.voucherPaidCents)}</dd></div>
          <div><dt>Left to pay</dt><dd>{formatMoney(payment.amountCents)}</dd></div>
        </dl>
      </div>

      {intent === null ? (
        <div className="panel stack" style={{ gap: 12 }}>
          <span className="label-text">Add a tip?</span>
          <div className="chips tip-chips" role="radiogroup" aria-label="Tip">
            {TIP_PERCENTS.map((pct) => (
              <button
                key={pct}
                type="button"
                role="radio"
                aria-checked={tipChoice === pct}
                className={tipChoice === pct ? 'chip on' : 'chip'}
                onClick={() => setTipChoice(pct)}
              >
                {pct === 0 ? 'No tip' : `${pct}% · ${formatMoney(Math.round((payment.billCents * pct) / 100))}`}
              </button>
            ))}
            <button
              type="button"
              role="radio"
              aria-checked={tipChoice === 'custom'}
              className={tipChoice === 'custom' ? 'chip on' : 'chip'}
              onClick={() => setTipChoice('custom')}
            >
              Other
            </button>
          </div>

          {tipChoice === 'custom' && (
            <label className="field">
              <span className="label-text">Tip amount ($)</span>
              <input inputMode="decimal" value={customTip} onChange={(e) => setCustomTip(e.target.value)} />
            </label>
          )}

          <p className="faint" style={{ margin: 0 }}>Tips go to {payment.businessName} in full.</p>

          {error !== null && <p className="notice error">{error}</p>}

          <button type="button" className="btn block" disabled={starting || total <= 0} onClick={() => void start()}>
            {starting ? 'One moment…' : total <= 0 ? 'Nothing to pay' : `Continue to pay ${formatMoney(total)}`}
          </button>
        </div>
      ) : (
        stripePromise !== null && (
          <Elements stripe={stripePromise} options={{ clientSecret: intent.clientSecret, appearance: { theme: 'stripe' } }}>
            <PayForm
              totalCents={intent.totalCents}
              onPaid={() => { setPayment({ ...payment, status: 'processing' }); }}
              onChangeTip={() => setIntent(null)}
            />
          </Elements>
        )
      )}
    </div>
  );
}

function PayForm({
  totalCents,
  onPaid,
  onChangeTip,
}: {
  totalCents: number;
  onPaid: () => void;
  onChangeTip: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [expressReady, setExpressReady] = useState(false);

  // One confirm path for the card form and the wallet buttons - see Checkout.tsx.
  const confirm = useCallback(async () => {
    if (stripe === null || elements === null || busy) return;

    setBusy(true);
    setMessage(null);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });

    if (error) {
      setMessage(
        error.type === 'card_error' || error.type === 'validation_error'
          ? error.message ?? 'That payment did not go through.'
          : 'Something went wrong taking payment. Ask the cashier before trying again.',
      );
      setBusy(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded' || paymentIntent?.status === 'processing') {
      onPaid();
      return;
    }

    setMessage('That payment was not completed. Please try again.');
    setBusy(false);
  }, [stripe, elements, busy, onPaid]);

  return (
    <form className="panel stack" style={{ gap: 18 }} onSubmit={(e) => { e.preventDefault(); void confirm(); }}>
      <ExpressCheckoutElement
        options={{ buttonHeight: 48 }}
        onReady={({ availablePaymentMethods }) => {
          setExpressReady(availablePaymentMethods !== undefined && Object.keys(availablePaymentMethods).length > 0);
        }}
        onConfirm={() => void confirm()}
      />

      {expressReady && <p className="or-divider"><span>or pay by card</span></p>}

      <PaymentElement />

      {message !== null && <p className="notice error">{message}</p>}

      <button type="submit" className="btn block" disabled={stripe === null || busy}>
        {busy ? 'Processing…' : `Pay ${formatMoney(totalCents)}`}
      </button>

      <button type="button" className="btn secondary block" disabled={busy} onClick={onChangeTip}>
        Change the tip
      </button>

      <p className="faint center">
        Card details go straight to our payment provider. They never touch our servers.
      </p>
    </form>
  );
}
